<template>
  <div class="kpt-list-page">
    <vue-headful title="Terminals" />
    <header class="kpt-list-header">
      <h4>Terminals</h4>
      <p class="is-size-6">Count: {{ total }}</p>
    </header>
    <div class="is-flex p-3 is-align-items-center">
      <div class="buttons mr-2 my-0">
        <b-button size="is-small" icon-left="sync" @click="getTerminals" class="mb-0">
          Refresh List
        </b-button>
        <b-button size="is-small" icon-left="file-csv" @click="exportCsv" class="mb-0">
          Export CSV
        </b-button>
      </div>
      <b-input size="is-small" placeholder="Filter..." v-model="query" @keyup.native.enter="runQueryFilter" style="width: 200px"></b-input>
      <b-select size="is-small" placeholder="Filter by Account" class="mx-2" @input="(v) => toggleAccountFilter(v)">
        <option :value="null">All</option>
        <option
            v-for="account in accounts"
            :value="account.id"
            :key="account.id">
            {{ account.name }}
        </option>
      </b-select>
      <b-switch size="is-small" v-model="filters" native-value="NetworkStatus.CellularOnline:true">
        Cellular Online
      </b-switch>
      <b-switch size="is-small" v-model="filters" native-value="NetworkStatus.SatelliteOnline:true">
        Satellite Online
      </b-switch>
    </div>
    <div class="kpt-list-table">
      <b-table
        :data="terminals"
         sticky-header
        :narrowed="true"
        :hoverable="true"
        :loading="isLoading"

        sort-icon="chevron-up"
        sort-icon-size="is-small"
        backend-sorting
        :default-sort="['Name', 'asc']"
        default-sort-direction="asc"
        @sort="onSort"

        paginated
        backend-pagination
        :per-page="pageSize"
        :total="total"
        @page-change="onPageChange"
        
        class="has-horizontal-scroll is-full-height">
        <b-table-column field="NetworkStatus.SatelliteOnline" label="Satellite" width="32">
          <template v-slot:header="{  }">
            <font-awesome-icon :icon="['far', 'satellite-dish']" title="Satellite Status" />
          </template>
          <template v-slot="props">
            <font-awesome-icon :icon="['fas', 'circle']" class="has-text-success" v-if="props.row.networkStatus?.satelliteOnline==true" title="Online" />
            <font-awesome-icon :icon="['far', 'circle']" class="has-text-danger" v-else-if="props.row.networkStatus?.satelliteOnline==false" title="Offline" />
            <font-awesome-icon :icon="['fas', 'circle']" class="has-text-grey-light" v-else title="Unknown" />
          </template>
        </b-table-column>
        <b-table-column field="NetworkStatus.CellularOnline" label="Cellular" width="32">
          <template v-slot:header="{  }">
            <font-awesome-icon :icon="['far', 'signal-stream']" title="Cellular Status" />
          </template>
          <template v-slot="props">
            <font-awesome-icon :icon="['fas', 'circle']" class="has-text-success" v-if="props.row.networkStatus?.cellularOnline==true" title="Online" />
            <font-awesome-icon :icon="['far', 'circle']" class="has-text-danger" v-else-if="props.row.networkStatus?.cellularOnline==false" title="Offline" />
            <font-awesome-icon :icon="['fas', 'circle']" class="has-text-grey-light" v-else title="Unknown" />
          </template>
        </b-table-column>
        <b-table-column field="Name" label="Name" v-slot="props" sortable>
          <router-link :to="{ name: 'terminalOverview', params: { id: props.row.id } }">{{props.row.name || 'Unknown'}}</router-link>
        </b-table-column>
        <b-table-column field="AccountName" label="Account" v-slot="props" sortable>
          {{ props.row.accountName || getAccountNameFromId(props.row.accountId) }}
        </b-table-column>
        <b-table-column field="Serial" label="Serial" v-slot="props" width="200">
          <span @click.ctrl="deleteTerminal(props.row.id)">
            {{ props.row.serial || props.row.hardware?.antenna?.serial }}
          </span>
        </b-table-column>
        <b-table-column field="Hardware.GeoModem.Serial" label="Modem Serial" v-slot="props" width="200">
            {{ props.row.hardware?.geoModem?.serial || '-' }}
        </b-table-column>
        <template slot="empty">
          <div class="p-6 is-flex is-justify-content-center">
            <p>No terminals were found.</p>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>
<script setup>
import { 
  onMounted, ref, computed, watch
} from 'vue';
import { useTerminalsStore } from './terminals-store';
import { useAccountsStore } from '../accounts/accounts-store';
import getAccountNameFromId from '../utilities/getAccountNameFromId';
import exportCsvArray from '../utilities/exportCsvArray';

const store = useTerminalsStore();
const accountsStore = useAccountsStore();
const terminals = computed(() => store.state.terminals);
const accounts = computed(() => { 
  const accountsFromState = accountsStore.state.accounts; 
  const sortedAccounts = accountsFromState.sort((a, b) => a.name.localeCompare(b.name));
  return sortedAccounts; 
});
const total = computed(() => store.state.terminalsTotal);
const isLoading = computed(() => store.state.terminalsLoading);

const currentPage = ref(1);
const pageSize = ref(50);
const sortField = ref('Name');
const sortDirection = ref('ASC');
const query = ref('');
const filters = ref([]);

const getTerminals = async () => {
  if (!pageSize.value) pageSize.value = 50;
  await store.dispatch('fetchTerminals', { 
    skip: (currentPage.value - 1) * pageSize.value,
    take: pageSize.value, 
    sortField: sortField.value, 
    sortDirection: sortDirection.value,
    fields: 'Id,Name,Serial,AccountId,AccountName,NetworkStatus,Hardware',
    query: query.value,
    filters: filters.value.join(',')
  });
}

const onSort = async (field, order) => {
  sortField.value = field;
  sortDirection.value = order.toUpperCase();
  await getTerminals();
}
const onPageChange = async (page) => {
  currentPage.value = page;
  await getTerminals();
}
const toggleAccountFilter = async (value) => {
  filters.value = filters.value.filter(v => !v.includes('AccountId'));
  if (value) {
    filters.value.push(`AccountId:${value}`);
  }
}
watch(filters, async () => {
  await getTerminals();
})

const runQueryFilter = async () => {
  currentPage.value = 1;
  await getTerminals();
}

const deleteTerminal = async (id) => {
  await store.dispatch('deleteTerminal', id);
}

const exportCsv = async () => {
  // fetch all
  const result = await store.dispatch('fetchTerminals', {
    skip: 0,
    take: 10000,
    sortField: sortField.value,
    sortDirection: sortDirection.value,
    skipCommit: true
  });

  const headers = ['Id', 'Name', 'Serial', 'KPC', 'Account', 'SatelliteOnline', 'CellularOnline', 'GeoModem.Serial', 'Antenna.Serial', 'HybridRouter.Serial'];
  const payload = result.items?.map(i => (Object.values({
    id: i.id,
    name: i.name,
    serial: i.serial,
    kpc: i.productCode,
    account: getAccountNameFromId(i.accountId),
    satelliteOnline: i.networkStatus?.satelliteOnline,
    cellularOnline: i.networkStatus?.cellularOnline,
    modemSerial: i.hardware?.geoModem?.serial,
    antennaSerial: i.hardware?.antenna?.serial,
    hybridRouterSerial: i.hardware?.hybridRouter?.serial
  })));

  // export
  await exportCsvArray(headers, payload, 'terminals');
}

onMounted(async () => {
  await getTerminals()
});
</script>
